import format from "date-fns/format";
import { commercialRepOptions, commercialRepIds } from "../data/portalFormListData";

// Read keys tolerate API casing/underscore differences; write keys are explicit.
export const TASK_PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const dash = (v) => (v === 0 ? "0" : v ? String(v) : "–");
const parseDateTime = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
};
const keyOf = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, "");
export const read = (record, ...keys) => {
    if (!record || typeof record !== "object") return undefined;
    const indexed = Object.fromEntries(Object.entries(record).map(([key, value]) => [keyOf(key), value]));
    for (const key of keys) {
        if (indexed[keyOf(key)] != null) return indexed[keyOf(key)];
    }
};
export const lookupId = (value) => (typeof value === "object" ? read(value, "id") : value);
export const lookupName = (value) => (typeof value === "object" ? read(value, "name", "fullName") || "" : value || "");

const joinName = (first, last) => [first, last].filter(Boolean).join(" ").trim();

// The lookup `name` is surname only; first/last names come back flattened on the record.
export const personName = (record, ...lookupKeys) => {
    for (const key of lookupKeys) {
        const name = joinName(read(record, `${key}.first_name`), read(record, `${key}.last_name`));
        if (name) return name;
    }
    const lookup = read(record, ...lookupKeys);
    if (lookup == null) return "";
    if (typeof lookup !== "object") return String(lookup).trim();
    const full = read(lookup, "fullName", "full_Name", "displayName");
    if (full) return String(full).trim();
    return (
        joinName(read(lookup, "firstName", "first_Name"), read(lookup, "lastName", "last_Name")) || lookupName(lookup)
    );
};

export const isUnassignedTask = (task) =>
    !task.complete && !task.commercialRepId && (!String(task.commercialRep || "").trim() || task.commercialRep === "–");
export const countNewTasks = (tasks) => (Array.isArray(tasks) ? tasks.filter(isUnassignedTask).length : 0);

// Continuous airport path from a deal's legs (e.g. ["EGSS", "LSGG", "EGSS"]).
export const routeFromDealLegs = (deal) => {
    const legs = Array.isArray(deal?.legs) ? [...deal.legs].sort((a, b) => (a.legNumber < b.legNumber ? -1 : 1)) : [];
    const points = [];
    legs.forEach((leg, i) => {
        if (i === 0 && leg.legFrom) points.push(leg.legFrom);
        if (leg.legTo) points.push(leg.legTo);
    });
    return points;
};

// ICAO codes from a CRM deal name such as "EGKB to LIPZ, then LEIB to MYEH for …".
export const routeFromDealName = (name) => {
    const routePart = String(name || "").split(/\s+for\s+/i)[0];
    return routePart.match(/\b[A-Z]{4}\b/g) || [];
};

const routeFromRecord = (rawRoute, deal, record) => {
    if (Array.isArray(rawRoute)) return rawRoute;
    if (typeof rawRoute === "string") return rawRoute.split(/\s*(?:–|→| - )\s*/).filter(Boolean);
    const fromLegs = routeFromDealLegs(deal && typeof deal === "object" ? deal : record);
    if (fromLegs.length) return fromLegs;
    return routeFromDealName(typeof deal === "object" ? lookupName(deal) : deal);
};

export const normalizeTask = (record) => {
    const deal = read(record, "relatedDeal", "deal", "dealName");
    const rep = read(record, "assignedCommercialExec", "commercialRep", "commercialExecAssigned");
    const rawRoute = read(record, "route");
    const priority = String(read(record, "priority") || "");
    const complete = read(record, "complete");
    const repId =
        read(record, "commercialRepId", "assignedCommercialExecId") || (typeof rep === "object" ? lookupId(rep) : "");
    const dealTripId = typeof deal === "object" ? read(deal, "avinodeTripId", "aviTripId") : "";
    return {
        id: String(read(record, "id") || ""),
        dealId: String(read(record, "dealId") || lookupId(deal) || ""),
        tripId: read(record, "tripId", "avinodeTripId", "aviTripId") || dealTripId || "",
        route: routeFromRecord(rawRoute, deal, record),
        dateSubmitted: read(record, "dateSubmitted", "createdTime", "createdDate", "dateCreated") || "",
        task: read(record, "actionRequired", "task") || "",
        broker: personName(record, "salesPerson", "broker"),
        priority: TASK_PRIORITY_OPTIONS.find((option) => option.toLowerCase() === priority.toLowerCase()) || priority,
        commercialRep:
            lookupName(rep) ||
            Object.keys(commercialRepIds).find((name) => commercialRepIds[name] === repId) ||
            repId ||
            "",
        commercialRepId: repId,
        commercialNotes: read(record, "commNote", "commercialNotes") || "",
        complete: complete === true || String(complete).toLowerCase() === "true",
    };
};

export const normalizeTasksResponse = (data) => {
    if (Array.isArray(data)) return data.map(normalizeTask);
    const rows = read(data, "tasks", "commTasks", "data");
    if (rows != null && rows !== data) return normalizeTasksResponse(rows);
    if (data == null || data === "") return [];
    throw new Error("Unexpected commercial task response");
};

// ── Accessors (display) ────────────────────────────────────────────────────
// Date on line one, time on line two (stacked) — same shape as the Checklist.
const getDateSubmittedDisplay = (task) => {
    const dt = parseDateTime(task.dateSubmitted);
    return dt ? [format(dt, "dd/MM/yyyy"), format(dt, "HH:mm")] : ["–"];
};

// Continuous path (e.g. "EGSS–LSGG"), split into lines of up to 3 airports —
// matches the Checklist route rendering + colours.
const getRouteDisplay = (task) => {
    const points = Array.isArray(task.route) ? task.route.filter(Boolean) : [];
    if (!points.length) return ["–"];
    const lines = [];
    for (let i = 0; i < points.length; i += 3) {
        lines.push(points.slice(i, i + 3).join("–"));
    }
    return lines;
};

const getTripId = (task) => dash(task.tripId);

export const taskDealPath = (task) => `/portal/deal-details/sales/${task.dealId}`;

// ── Column definitions ───────────────────────────────────────────────────────
export const TASK_COLUMNS = [
    {
        key: "complete",
        label: "Complete",
        accessor: (t) => (t.complete ? "Yes" : "No"),
        editable: {
            type: "select",
            options: ["Yes", "No"],
            dealKey: "complete",
            payloadKey: "Complete",
            valueType: "yesNo",
            required: true,
        },
    },
    {
        key: "dateSubmitted",
        label: "Task Date & Time",
        accessor: getDateSubmittedDisplay,
        isStacked: true,
        sortable: true,
    },
    { key: "route", label: "Route", accessor: getRouteDisplay, isDealLink: true },
    { key: "tripId", label: "Trip ID", accessor: getTripId, isAvinode: true },
    {
        key: "task",
        label: "Task",
        accessor: (t) => dash(t.task),
        editable: { type: "textarea", maxLength: 2000, dealKey: "task", payloadKey: "Action_Required" },
    },
    { key: "broker", label: "Broker", accessor: (t) => dash(t.broker), sortable: true },
    {
        key: "priority",
        label: "Priority",
        accessor: (t) => dash(t.priority),
        sortable: true,
        editable: {
            type: "select",
            options: TASK_PRIORITY_OPTIONS,
            dealKey: "priority",
            payloadKey: "Priority",
            required: true,
        },
    },
    {
        key: "commercialRep",
        label: "Commercial Rep",
        accessor: (t) => dash(t.commercialRep),
        sortable: true,
        editable: {
            type: "select",
            options: commercialRepOptions,
            dealKey: "commercialRep",
            payloadKey: "Assigned_Commercial_Exec",
            valueType: "commercialRep",
            required: true,
        },
    },
    {
        key: "commercialNotes",
        label: "Commercial Notes",
        accessor: (t) => dash(t.commercialNotes),
        editable: { type: "textarea", maxLength: 2000, dealKey: "commercialNotes", payloadKey: "Comm_Note" },
    },
];

// ── Sorting ───────────────────────────────────────────────────────────────────
// High → Medium → Low; anything unrecognised sorts after the known priorities.
const priorityRank = (t) => {
    const i = TASK_PRIORITY_OPTIONS.indexOf(t.priority);
    return i === -1 ? TASK_PRIORITY_OPTIONS.length : i;
};

const SORT_ACCESSORS = {
    dateSubmitted: (t) => parseDateTime(t.dateSubmitted),
    broker: (t) => t.broker,
    priority: priorityRank, // High → Low
    commercialRep: (t) => t.commercialRep,
};

const isNil = (v) => v == null || v === "" || v === "–";

const compareValues = (a, b) => {
    if (isNil(a) && isNil(b)) return 0;
    if (isNil(a)) return 1;
    if (isNil(b)) return -1;
    if (a instanceof Date && b instanceof Date) return a - b;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

// Oldest task first — the earliest-created task sits at the top of its priority band.
const compareByCreatedAsc = (a, b) => compareValues(parseDateTime(a.dateSubmitted), parseDateTime(b.dateSubmitted));

// Default order: priority High → Medium → Low, then (within a priority) the
// earliest-created task first.
const compareByPriorityThenCreated = (a, b) => {
    const res = compareValues(priorityRank(a), priorityRank(b));
    return res !== 0 ? res : compareByCreatedAsc(a, b);
};

export const sortTasks = (tasks, sort) => {
    const list = Array.isArray(tasks) ? [...tasks] : [];
    if (!sort || !sort.key) return list.sort(compareByPriorityThenCreated);
    const accessor = SORT_ACCESSORS[sort.key];
    if (!accessor) return list.sort(compareByPriorityThenCreated);
    const factor = sort.dir === "desc" ? -1 : 1;
    return list.sort((a, b) => {
        const res = compareValues(accessor(a), accessor(b));
        return res !== 0 ? res * factor : compareByPriorityThenCreated(a, b);
    });
};

export const distinctTaskBrokers = (tasks) =>
    [...new Set((tasks || []).map((t) => String(t.broker ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
    );

export const filterTasks = (tasks, { search, priorities, commercials, brokers } = {}) => {
    if (!Array.isArray(tasks)) return [];
    const q = (search || "").trim().toLowerCase();

    return tasks.filter((t) => {
        if (q) {
            const hay =
                `${t.task ?? ""} ${t.commercialRep ?? ""} ${t.broker ?? ""} ${t.tripId ?? ""} ${(t.route || []).join(" ")}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        if (priorities && priorities.length && !priorities.includes(t.priority)) return false;
        if (commercials && commercials.length && !commercials.includes(t.commercialRep)) return false;
        if (brokers && brokers.length && !brokers.includes(String(t.broker ?? "").trim())) return false;
        return true;
    });
};
